import type { ReactNode } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { CalendarPostRisk } from "@/types/contentCalendar.type";
import StageTrack from "../components/StageTrack";
import { TurnDot } from "../components/DateRail";
import { WEEKDAY_SHORT, dayMonthShort, listGroups, pillarHue, stageWord, type CalendarPost } from "./calendarModel";
import { formatLabel, peekHandlers, type PeekIntent } from "./CalendarPostCard";
import { ChannelMarks } from "./CalendarParts";
import { statePrimary } from "./CalendarStates";

/** Calendar / List row: Day 64, Post, Stage 180, Next 240, When 190, 20 apart. */
const COLUMNS = "grid grid-cols-[64px_minmax(0,1fr)_180px_240px_190px] items-center gap-x-5";

export type RiskDecision = (risk: CalendarPostRisk, decision: "accept" | "dismiss") => void;

/**
 * CalendarListRow (library Calendar / List row): one post as a row. The day,
 * the pillar as a 3px edge beside the title and named in the meta line, the
 * stage track with its word, whose turn it is, and when: the state in a few
 * words, or the at-risk reason in alert ink.
 */
export function CalendarListRow({
  post,
  anchor,
  isGuest,
  now,
  active,
  onOpen,
  peek,
  onDecideRisk,
}: {
  /** Hover or keyboard focus shows the row's peek. */
  peek?: PeekIntent;
  /** Accept or dismiss the at-risk suggestion under the row (backend #126). */
  onDecideRisk?: RiskDecision;
  post: CalendarPost;
  /** The day anchor, on the first row of each day only. */
  anchor: { day: number; weekday: number } | null;
  isGuest: boolean;
  now: number;
  active: boolean;
  onOpen: (post: CalendarPost, anchor: HTMLElement) => void;
}) {
  const { slot, state } = post;
  const hue = pillarHue(post.pillarIndex);
  const meta = [formatLabel(slot), slot.pillarName ? slot.pillarName.toLowerCase() : null].filter(Boolean).join(" · ");
  const risk = slot.atRisk && slot.atRisk.status === "open" ? slot.atRisk : null;
  const yours = state.turn.owner === "you";
  return (
    <>
    <div
      role="button"
      tabIndex={0}
      data-post-id={slot.id}
      aria-label={`${slot.title}. ${meta}. ${stageWord(state, isGuest)}, ${state.turn.label}.`}
      onClick={(event) => onOpen(post, event.currentTarget)}
      {...peekHandlers(peek)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(post, event.currentTarget);
        }
      }}
      className={cn(
        COLUMNS,
        "relative cursor-pointer py-3 outline-none transition-colors",
        // The row's hairline; a suggestion card under it takes its place.
        !(risk && onDecideRisk) && "shadow-[inset_0_-1px_0_var(--co-line)]",
        "hover:bg-co-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
        active && "bg-co-hover",
      )}
    >
      <span className="flex flex-col gap-0.5">
        {anchor ? (
          <>
            <span className="font-section text-[26px] font-bold leading-none tabular-nums text-co-text">{anchor.day}</span>
            <span className="text-[12.5px] leading-[17px] text-co-muted">{WEEKDAY_SHORT[anchor.weekday]}</span>
          </>
        ) : null}
      </span>
      {hue ? (
        <span aria-hidden="true" className="absolute left-[70px] top-3.5 h-[37px] w-[3px]" style={{ background: hue }} />
      ) : null}
      <span className="flex min-w-0 flex-col gap-[5px]">
        <span className="text-[14px] font-semibold leading-[1.35] text-co-text">{slot.title}</span>
        <span className="flex min-w-0 items-center gap-2 text-[12.5px] leading-[17px] text-co-muted">
          <span className="truncate">{meta}</span>
          <ChannelMarks channels={post.channels} className="gap-2" />
        </span>
      </span>
      <span className="flex flex-col gap-1.5">
        <StageTrack stageIndex={state.stageIndex} stops={state.stops} size="card" className="gap-[3px]" />
        <span className="text-[12.5px] font-semibold text-co-text">{stageWord(state, isGuest)}</span>
      </span>
      <span
        className="flex min-w-0 items-center gap-1.5 text-[13.5px] font-semibold"
        style={{ color: state.turn.alert ? "var(--co-alert-ink)" : yours ? "var(--co-accent-ink)" : state.turn.owner === "none" ? "var(--co-muted)" : "var(--co-text2)" }}
      >
        <TurnDot owner={state.turn.alert ? "late" : state.turn.owner === "late" ? "client" : state.turn.owner} />
        <span className="min-w-0 truncate">{state.turn.label}</span>
        {yours ? <ArrowRight className="size-3.5 shrink-0 text-co-accent" aria-hidden="true" /> : null}
      </span>
      <span
        className="text-[13px] leading-[1.35]"
        style={{ color: risk || state.turn.owner === "late" ? "var(--co-alert-ink)" : "var(--co-text2)" }}
      >
        {risk ? `At risk · ${riskNote(risk, slot.scheduledAt, now)}` : state.banner.title}
      </span>
    </div>
    {risk && onDecideRisk ? <RiskSuggestion risk={risk} onDecide={onDecideRisk} /> : null}
    </>
  );
}

/** The list's column heads: Day, Post, Stage, Next, When. */
export function ListHeader() {
  return (
    <div
      className={cn(COLUMNS, "pb-2 pt-2.5 text-[12.5px] leading-[17px] text-co-muted shadow-[inset_0_-1px_0_var(--co-line)]")}
      aria-hidden="true"
    >
      <span>Day</span>
      <span>Post</span>
      <span>Stage</span>
      <span>Next</span>
      <span>When</span>
    </div>
  );
}

/** Opens or collapses a row in 200ms; reduced motion makes it instant. */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      aria-hidden={!open || undefined}
      inert={!open || undefined}
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-[var(--duration-short)] ease-[var(--ease-out-expo)] motion-reduce:transition-none",
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * The List view: rows grouped by day, with month dividers when the list runs
 * across months (the Needs you queue does). A filter collapses the rows it
 * leaves out rather than dropping them, so the list closes up in 200ms.
 */
export default function MonthList({
  posts,
  shown,
  isGuest,
  now,
  activePostId,
  showMonths,
  onOpen,
  peekFor,
  onDecideRisk,
}: {
  onDecideRisk?: RiskDecision;
  peekFor?: (post: CalendarPost) => PeekIntent;
  posts: CalendarPost[];
  /** The rows the filters keep; every row when absent. */
  shown?: ReadonlySet<string>;
  isGuest: boolean;
  now: number;
  activePostId: string | null;
  /** Month dividers even for one month, when the list is a cross-month queue. */
  showMonths: boolean;
  onOpen: (post: CalendarPost, anchor: HTMLElement) => void;
}) {
  const groups = listGroups(posts);
  const dividers = showMonths || groups.length > 1;
  const isShown = (post: CalendarPost) => !shown || shown.has(post.slot.id);
  return (
    <div className="flex min-w-0 flex-col">
      <ListHeader />
      {groups.map((group) => {
        const groupOpen = group.days.some((day) => day.posts.some(isShown));
        return (
          <section key={`${group.year}-${group.month}`} aria-label={group.label}>
            {dividers ? (
              <Collapse open={groupOpen}>
                <h2 className="pb-2 pt-[18px] font-section text-[20px] font-bold text-co-text">{group.label}</h2>
              </Collapse>
            ) : null}
            <div role="list" aria-label={`Posts in ${group.label}`}>
              {group.days.map((day) => {
                // The day anchor rides on the first row the filters keep.
                const first = day.posts.find(isShown) ?? day.posts[0];
                return day.posts.map((post) => (
                  <div role={isShown(post) ? "listitem" : "presentation"} key={post.slot.id}>
                    <Collapse open={isShown(post)}>
                      <CalendarListRow
                        post={post}
                        anchor={post === first ? { day: day.day, weekday: day.weekday } : null}
                        isGuest={isGuest}
                        now={now}
                        active={activePostId === post.slot.id}
                        onOpen={onOpen}
                        peek={peekFor?.(post)}
                        onDecideRisk={onDecideRisk}
                      />
                    </Collapse>
                  </div>
                ));
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

const FIX_ASK: Record<CalendarPostRisk["fix"]["kind"], string> = {
  pick: "Pick an idea now?",
  send: "Send the concept now?",
  remind: "Send a reminder?",
  send_brief: "Send the brief now?",
  move: "Move it?",
  ask_owner: "Ask the post's owner?",
};
const FIX_ACTION: Record<CalendarPostRisk["fix"]["kind"], string> = {
  pick: "Pick",
  send: "Send",
  remind: "Remind",
  send_brief: "Send the brief",
  move: "Move",
  ask_owner: "Ask",
};

/**
 * Calendar / M7 ask · Suggestion card under a row at risk: solid, radius 12,
 * the accent outline because M7 asks mean something. Its one primary accepts
 * the suggestion; Keep date (or Not now) dismisses it.
 */
function RiskSuggestion({ risk, onDecide }: { risk: CalendarPostRisk; onDecide: RiskDecision }) {
  const move = risk.fix.kind === "move" && risk.fix.day;
  const day = move ? new Date(risk.fix.day!) : null;
  const dayLabel = day ? dayMonthShort({ weekday: day.getUTCDay(), day: day.getUTCDate(), month: day.getUTCMonth() + 1 }) : null;
  return (
    <div className="pb-4 pl-[58px]">
      <div className="flex flex-col gap-2.5 rounded-card border border-co-accent-border bg-co-surface-solid px-4 py-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 shrink-0 text-co-accent" aria-hidden="true" />
          <span className="text-[14px] font-semibold text-co-text">{dayLabel ? `Move it to ${dayLabel}?` : FIX_ASK[risk.fix.kind]}</span>
          <span className="flex-1" />
          <span className="text-[12px] text-co-muted">From this morning’s check</span>
        </div>
        <p className="text-[13px] leading-[1.5] text-co-text2">{risk.reason}</p>
        <div className="flex items-center gap-2">
          <button type="button" className={statePrimary} onClick={() => onDecide(risk, "accept")}>
            {FIX_ACTION[risk.fix.kind]}
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onDecide(risk, "dismiss")}
            className="inline-flex h-8 items-center rounded-pill px-3.5 text-[13px] font-medium text-co-accent-ink outline-none hover:bg-co-hover focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            {move ? "Keep date" : "Not now"}
          </button>
          <span className="flex-1" />
          {move ? <span className="text-[12px] text-co-muted">A move can be undone.</span> : null}
        </div>
      </div>
    </div>
  );
}

const RISK_WORDS: Record<string, string> = {
  no_idea_picked: "no idea picked yet",
  concept_not_sent: "the concept is not sent",
  concept_with_client: "the concept is still with the client",
  brief_not_sent: "the brief is not sent",
  brief_with_client: "the brief is still with the client",
  not_ready: "not fully approved",
  ready_by_passed: "the ready-by date passed",
};

/** "no idea picked yet, goes live in 6 days": the risk in the row's few words. */
function riskNote(risk: CalendarPostRisk, scheduledAt: string, now: number): string {
  const what = RISK_WORDS[risk.risk] ?? "may miss its day";
  const day = new Date(scheduledAt);
  const today = new Date(now);
  const days = Math.round(
    (Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate()) -
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())) /
      86_400_000,
  );
  if (days < 0) return what;
  return `${what}, goes live ${days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`}`;
}
